import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Package, BarChart2, ShoppingCart, Database, Users, FileText, ScanLine, Bot } from "lucide-react";
import "./Features.scss";

const featureList = [
  { icon: <Package size={22} />,      key: "inventory",   color: "#2563eb", bg: "#eff6ff" },
  { icon: <ShoppingCart size={22} />, key: "orders",      color: "#7c3aed", bg: "#f5f3ff" },
  { icon: <Users size={22} />,        key: "patients",    color: "#059669", bg: "#ecfdf5" },
  { icon: <Database size={22} />,     key: "database",    color: "#d97706", bg: "#fffbeb" },
  { icon: <BarChart2 size={22} />,    key: "reports",     color: "#dc2626", bg: "#fef2f2" },
  { icon: <FileText size={22} />,     key: "invoicing",   color: "#0891b2", bg: "#ecfeff" },
  // Highlighted: this is the differentiator worth calling out, so it carries a "Nouveau"
  // badge and an accented card style (see .syd-feature-card--highlight in Features.scss).
  { icon: <ScanLine size={22} />,     key: "ocr",         color: "#4f46e5", bg: "#eef2ff", highlight: true },
  { icon: <Bot size={22} />,          key: "assistant",   color: "#db2777", bg: "#fce7f3" },
];

const Features = () => {
  const { t, i18n } = useTranslation();
  return (
    <section className="syd-features" id="features">
      <div className="syd-features__bg">
        <div className="syd-features__bg-orb syd-features__bg-orb--1" />
        <div className="syd-features__bg-orb syd-features__bg-orb--2" />
      </div>

      <div className="container">
        <motion.div
          className="syd-features__header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="syd-features__label">{t("landing.nav.features")}</div>
          <h2 className="syd-features__title">
            {t("landing.features.title")}
          </h2>
          <span className="syd-features__underline" aria-hidden="true" />
          <p className="syd-features__sub">
            {t("landing.features.sub")}
          </p>
        </motion.div>

      </div>

      {/* One row of cards scrolling on its own (CSS marquee, paused on hover). The list is
          rendered twice so the loop is seamless; the copy is hidden from assistive tech.
          The track stays LTR for the animation maths, each card gets the page direction. */}
      <motion.div
        className="syd-features__marquee"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="syd-features__track">
          {[...featureList, ...featureList].map((f, idx) => {
            const i = idx % featureList.length;
            const copy = idx >= featureList.length;
            return (
              <div
                className={`syd-feature-card${f.highlight ? " syd-feature-card--highlight" : ""}`}
                key={idx}
                dir={i18n.dir()}
                aria-hidden={copy || undefined}
                // per-feature accent: icon tile, hover border / top bar, number
                style={{ "--accent": f.color, "--accent-bg": f.bg }}
              >
                <div className="syd-feature-card__top">
                  <div className="syd-feature-card__icon">{f.icon}</div>
                  {f.highlight
                    ? <span className="syd-feature-card__badge">{t("landing.features.badge_new")}</span>
                    : <span className="syd-feature-card__num" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>}
                </div>
                <h3 className="syd-feature-card__title">{t(`landing.features.${f.key}_title`)}</h3>
                <p className="syd-feature-card__desc">{t(`landing.features.${f.key}_desc`)}</p>
              </div>
            );
          })}
        </div>
      </motion.div>
    </section>
  );
};

export default Features;
